import { FC, Fragment, useMemo } from 'react';

import {
  Alert,
  Box,
  IconButton,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { ExternalLink, X } from 'lucide-react';

import {
  IcAudioFile,
  IcDocument,
  IcPhoto,
  IcVideoRecorder,
} from '@/assets/images';
import { useFormConfigLoader } from '@/hooks';
import { useFetchSasToken } from '@/hooks/useFetchSaasToken';
import { IFile, IOffenseOptionItem } from '@/models';
import { getOffenseOptionItems } from '@/pages/Kases/KaseCrimes/utilities';
import { MasterIndexType } from '@/pages/MasterIndex/components/MasterIndexDetailsPage/MasterIndexDetailsPage';
import { getPlainTextFromDescription } from '@/pages/Notes/NotesDataGridWrapper';
import { getFileType, getFileUrlWithValidToken } from '@/utils';

import {
  AnyMasterRecord,
  MergeConflict,
  MergeFieldDef,
  MergeFormatContext,
  formatMergeValue,
  mergeConfigs,
} from './mergeFieldConfig';

interface MergeConflictStepProps {
  masterIndexType: MasterIndexType;
  selectedRecord: AnyMasterRecord;
  conflicts: MergeConflict[];
  choices: Record<string, 'current' | 'selected'>;
  onSetChoice: (key: string, side: 'current' | 'selected') => void;
  onSelectAll: (side: 'current' | 'selected') => void;
  onDeselect: () => void;
  onPreviewFile: (file: IFile, sasToken: string | undefined) => void;
  sourceNotes: any[];
  noteChecks: Record<string, boolean>;
  onToggleNote: (id: string) => void;
  sourceCautions: any[];
  cautionChecks: Record<string, boolean>;
  onToggleCaution: (id: string) => void;
  currentNotes: any[];
  currentCautions: any[];
  currentFlags: any[];
  sourceFlags: any[];
  flagChecks: Record<string, boolean>;
  onToggleFlag: (id: string) => void;
  currentRelatedRecords: any[];
  relatedRecords: any[];
  /**
   * SYSTEM-origin flags (e.g. WP) on each record — rendered read-only
   * because the BE registry rewriter reassigns them automatically.
   */
  currentSystemFlags?: any[];
  sourceSystemFlags?: any[];
  /**
   * True when either record has `addressPrePlans` entries for agencies
   * other than the active one (addresses only). Renders an inline warning
   * inside the Pre-Plan section row that those other-agency entries won't
   * be merged onto the surviving address.
   */
  prePlanHasOtherAgencyData?: boolean;
}

const FILE_TYPE_ICONS = {
  image: <IcPhoto width={20} height={20} />,
  video: <IcVideoRecorder width={20} />,
  audio: <IcAudioFile width={20} height={20} />,
  document: <IcDocument width={20} height={20} />,
  draft: <IcDocument width={20} height={20} />,
} as const;

const FileCellValue: FC<{
  files: IFile[];
  onPreview: (file: IFile, sasToken: string | undefined) => void;
}> = ({ files, onPreview }) => {
  const sasToken = useFetchSasToken(files);

  if (files.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No files
      </Typography>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {files.map((f) => {
        const fileType = getFileType(f.mimeType);
        const isImage = fileType === 'image';
        // Guard against undefined= in URL while token is still loading
        const src =
          isImage && sasToken
            ? getFileUrlWithValidToken(f, sasToken)
            : undefined;
        return (
          <Box
            key={f._id ?? f.name}
            title={f.name}
            onClick={(e) => {
              // Prevent the wrapping conflict-row cell from also handling
              // the click as a radio toggle.
              e.preventDefault();
              e.stopPropagation();
              onPreview(f, sasToken);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 0.5,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
              cursor: 'pointer',
              bgcolor: isImage ? undefined : 'action.hover',
              color: 'text.secondary',
            }}
          >
            {isImage && src ? (
              <Box
                component="img"
                src={src}
                alt={f.name}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              FILE_TYPE_ICONS[fileType]
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * Plain-text extraction for a note row.
 * `INote` (master-name / master-vehicle notes) stores its body in
 * `description`; `IAddressNote` uses `comment`. Both are typically Draft.js
 * raw-state JSON strings, so we run them through `getPlainTextFromDescription`.
 */
function renderNoteText(n: any): string {
  const raw =
    typeof n?.description === 'string'
      ? n.description
      : typeof n?.comment === 'string'
      ? n.comment
      : '';
  return raw ? getPlainTextFromDescription(raw) || raw : '';
}

/** Render one related-record row's inline content: type chip + natural id +
 *  external-link icon. Mongo `_id` and address/location are intentionally
 *  hidden — only the user-facing identifier and the open-in-new-tab control
 *  are shown. */
function renderRelatedContent(r: any): React.ReactNode {
  const typeLabel = typeof r?.type === 'string' && r.type ? r.type : null;
  const displayLabel = r?.displayId || r?._id;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ flexWrap: 'wrap' }}
    >
      {typeLabel && (
        <Typography
          variant="caption"
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {typeLabel}
        </Typography>
      )}
      <Typography variant="body2" fontWeight={500}>
        {displayLabel}
      </Typography>
      {r?.path && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.open(r.path, '_blank', 'noopener');
          }}
          title="Open in new tab"
        >
          <ExternalLink size={16} />
        </IconButton>
      )}
    </Stack>
  );
}

/** Compose "type · comment" label for a single caution row. */
function renderCautionLabel(c: any): string {
  const typeName =
    c?.type && typeof c.type === 'object' ? c.type.name ?? '' : c?.type ?? '';
  const commentText =
    typeof c?.comment === 'string'
      ? getPlainTextFromDescription(c.comment) || c.comment
      : '';
  return [typeName, commentText].filter(Boolean).join(' · ');
}

function renderCellValue(
  type: MasterIndexType,
  field: MergeFieldDef,
  value: unknown,
  onPreviewFile: (file: IFile, sasToken: string | undefined) => void,
  ctx?: MergeFormatContext,
): React.ReactNode {
  if (field.renderType === 'photo') {
    const files = (Array.isArray(value) ? value : []) as IFile[];
    return <FileCellValue files={files} onPreview={onPreviewFile} />;
  }

  // Synthesized rows (e.g. pre-plan subfields) supply their own formatter
  // because their `key` isn't known to the per-type dispatch in
  // `formatMergeValue`. The formatter may return null to fall through to
  // the default dispatch (handy for plain strings/numbers).
  let formatted: string | null = null;
  if (field.formatter) {
    formatted = field.formatter(value);
  }
  if (formatted === null) {
    formatted = formatMergeValue(type, field.key, value, ctx);
  }

  return (
    <Typography
      variant="body2"
      sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
    >
      {formatted}
    </Typography>
  );
}

export const MergeConflictStep: FC<MergeConflictStepProps> = ({
  masterIndexType,
  selectedRecord,
  conflicts,
  choices,
  onSetChoice,
  onSelectAll,
  onDeselect,
  onPreviewFile,
  sourceNotes,
  noteChecks,
  onToggleNote,
  sourceCautions,
  cautionChecks,
  onToggleCaution,
  currentNotes,
  currentCautions,
  currentFlags,
  sourceFlags,
  flagChecks,
  onToggleFlag,
  currentRelatedRecords,
  relatedRecords,
  currentSystemFlags = [],
  sourceSystemFlags = [],
  prePlanHasOtherAgencyData = false,
}) => {
  const theme = useTheme();
  const config = mergeConfigs[masterIndexType];
  const TypeIcon = config.icon;
  const title = config.getRecordTitle(selectedRecord);
  const subtitle = config.getRecordSubtitle(selectedRecord);

  const { filteredOptions } = useFormConfigLoader({
    optionsName: 'PROPERTY',
    category: ['PROPERTY'],
  });

  const formatCtx = useMemo<MergeFormatContext>(() => {
    if (masterIndexType !== 'vehicles') return {};
    const propertyTypeMap = getOffenseOptionItems(
      'propertyType',
      filteredOptions,
    ) as Map<string, IOffenseOptionItem>;
    const vehicleTypeLabels: Record<string, string> = {};
    propertyTypeMap.forEach((item) => {
      if (item.key) vehicleTypeLabels[item.key] = item.label;
    });
    return { vehicleTypeLabels };
  }, [masterIndexType, filteredOptions]);

  const getRecordPath = (): string => {
    const id = (selectedRecord as any)._id ?? (selectedRecord as any).id;
    switch (masterIndexType) {
      case 'organizations':
        return `/master-index/organizations/${id}/view/information`;
      case 'vehicles':
        return `/master-index/vehicles/${id}/information`;
      case 'address':
        return `/master-index/address/${id}/view/information`;
      case 'persons':
      default:
        return `/master-index/persons/${id}/view`;
    }
  };

  const allCurrentSelected =
    conflicts.length > 0 &&
    conflicts.every(({ field }) => choices[field.key] === 'current');
  const allSelectedSelected =
    conflicts.length > 0 &&
    conflicts.every(({ field }) => choices[field.key] === 'selected');

  const conflictsBySection = useMemo(() => {
    const map = new Map<string, typeof conflicts>();
    for (const c of conflicts) {
      const section = c.field.section ?? 'Other';
      const bucket = map.get(section) ?? [];
      bucket.push(c);
      map.set(section, bucket);
    }
    return map;
  }, [conflicts]);

  // Renders a "<radio> <value>" cell. The cell itself is the click target so
  // the handler fires on every click — including on an already-checked radio.
  // (MUI Radio's `onChange` only fires on the unchecked→checked transition,
  // so binding the toggle handler to `onChange` makes the cell feel inert
  // when the user clicks the side that's already selected.) The Radio is
  // rendered as a controlled, presentation-only indicator.
  const renderRadioCell = (
    checked: boolean,
    disabled: boolean,
    onActivate: () => void,
    content: React.ReactNode,
  ) => (
    <TableCell
      sx={{
        verticalAlign: 'top',
        pt: 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
      onClick={
        disabled
          ? undefined
          : (e) => {
              // Inner controls (e.g. photo preview boxes) opt out via
              // stopPropagation so clicking them doesn't also toggle the row.
              if (e.defaultPrevented) return;
              onActivate();
            }
      }
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={0.5}
        sx={{ minWidth: 0 }}
      >
        <Radio
          size="small"
          color="error"
          checked={checked}
          disabled={disabled}
          onChange={() => {}}
          sx={{ p: '4px', flexShrink: 0, mt: '-2px', pointerEvents: 'none' }}
        />
        {content}
      </Stack>
    </TableCell>
  );

  const emDash = (
    <Typography variant="body2" color="text.secondary">
      -
    </Typography>
  );

  const selectAllButtonSx = {
    color: theme.palette.error.main,
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    p: 0,
    fontFamily: 'inherit',
    '&:hover': { color: theme.palette.error.dark },
  };

  return (
    <Stack spacing={2}>
      {/* Selected record card */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
              <TypeIcon
                sx={{
                  fontSize: 14,
                  color: theme.palette.text.secondary,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                {subtitle}
              </Typography>
            </Stack>
          )}
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ ml: 1, flexShrink: 0 }}
        >
          <IconButton
            size="small"
            onClick={() => window.open(getRecordPath(), '_blank')}
            title="Open in new tab"
          >
            <ExternalLink size={16} color={theme.palette.text.secondary} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onDeselect}
            title="Remove selection"
          >
            <X size={16} />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }} />

      <Typography variant="body2" fontWeight={600}>
        Review Conflicts
      </Typography>

      <Alert severity="info" variant="outlined">
        The two records have different values for the fields below. Select which
        value to keep for each field. Fields with no conflict will be merged
        automatically.
      </Alert>

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 130 }} />
            <col />
            <col />
          </colgroup>

          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.action.hover,
                height: 36,
              }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  Fields
                </Typography>
              </TableCell>

              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontWeight={500}>
                    Current record
                  </Typography>
                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      onSelectAll(allCurrentSelected ? 'selected' : 'current')
                    }
                    sx={selectAllButtonSx}
                  >
                    {allCurrentSelected ? 'Unselect all' : 'Select all'}
                  </Box>
                </Stack>
              </TableCell>

              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontWeight={500}>
                    Matched record
                  </Typography>
                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      onSelectAll(allSelectedSelected ? 'current' : 'selected')
                    }
                    sx={selectAllButtonSx}
                  >
                    {allSelectedSelected ? 'Unselect all' : 'Select all'}
                  </Box>
                </Stack>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {Array.from(conflictsBySection.entries()).map(([section, rows]) => (
              <Fragment key={section}>
                <TableRow
                  sx={{
                    backgroundColor: theme.palette.action.selected,
                  }}
                >
                  <TableCell colSpan={3}>
                    <Typography variant="body2" fontWeight={600}>
                      {section}
                    </Typography>
                  </TableCell>
                </TableRow>
                {section === 'Pre-Plan' && prePlanHasOtherAgencyData && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 1 }}>
                      <Alert severity="warning" variant="outlined">
                        One or both of these addresses have pre-plan information
                        for other agencies. That information is not part of this
                        merge and will not be carried onto the surviving record.
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map(({ field, currentValue, selectedValue }) => (
                  <TableRow
                    key={field.key}
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                      height: 36,
                    }}
                  >
                    <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {field.label}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={0.5}
                        component="label"
                        sx={{ cursor: 'pointer', minWidth: 0 }}
                      >
                        <Radio
                          size="small"
                          color="error"
                          checked={choices[field.key] === 'current'}
                          onChange={() => onSetChoice(field.key, 'current')}
                          sx={{ p: '4px', flexShrink: 0, mt: '-2px' }}
                        />
                        {renderCellValue(
                          masterIndexType,
                          field,
                          currentValue,
                          onPreviewFile,
                          formatCtx,
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={0.5}
                        component="label"
                        sx={{ cursor: 'pointer', minWidth: 0 }}
                      >
                        <Radio
                          size="small"
                          color="error"
                          checked={choices[field.key] === 'selected'}
                          onChange={() => onSetChoice(field.key, 'selected')}
                          sx={{ p: '4px', flexShrink: 0, mt: '-2px' }}
                        />
                        {renderCellValue(
                          masterIndexType,
                          field,
                          selectedValue,
                          onPreviewFile,
                          formatCtx,
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}

            {/* Standalone Pre-Plan warning when neither record has a
                current-agency entry (so no conflict rows render under
                `Pre-Plan` above) but one or both has entries for other
                agencies. The warning lives in its own minimal section so
                the user still sees that other-agency data won't transfer. */}
            {prePlanHasOtherAgencyData &&
              !conflictsBySection.has('Pre-Plan') && (
                <Fragment key="__preplan_warning_only">
                  <TableRow
                    sx={{ backgroundColor: theme.palette.action.selected }}
                  >
                    <TableCell colSpan={3}>
                      <Typography variant="body2" fontWeight={600}>
                        Pre-Plan
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 1 }}>
                      <Alert severity="warning" variant="outlined">
                        One or both of these addresses have pre-plan information
                        for other agencies. That information is not part of this
                        merge and will not be carried onto the surviving record.
                      </Alert>
                    </TableCell>
                  </TableRow>
                </Fragment>
              )}

            {/* Notes - list current and matched notes together. Current-side
                rows have disabled radios (the note stays on the merged
                record); matched-side rows have enabled radios (default
                matched = bring over). Hide the section when neither record
                has notes. */}
            {(currentNotes.length > 0 || sourceNotes.length > 0) && (
              <Fragment key="__notes">
                <TableRow
                  sx={{ backgroundColor: theme.palette.action.selected }}
                >
                  <TableCell colSpan={3}>
                    <Typography variant="body2" fontWeight={600}>
                      Notes
                    </Typography>
                  </TableCell>
                </TableRow>

                {currentNotes.map((n: any) => {
                  const text = renderNoteText(n);
                  const keep = noteChecks[String(n._id)] !== false;
                  const content = (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {text || '(empty)'}
                    </Typography>
                  );
                  return (
                    <TableRow
                      key={`current-note-${n._id}`}
                      sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                    >
                      <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Note
                        </Typography>
                      </TableCell>
                      {renderRadioCell(
                        keep,
                        false,
                        () => onToggleNote(String(n._id)),
                        content,
                      )}
                      {renderRadioCell(
                        !keep,
                        false,
                        () => onToggleNote(String(n._id)),
                        emDash,
                      )}
                    </TableRow>
                  );
                })}

                {sourceNotes.map((n: any) => {
                  const text = renderNoteText(n);
                  const include = !!noteChecks[n._id];
                  const content = (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {text || '(empty)'}
                    </Typography>
                  );
                  return (
                    <TableRow
                      key={`source-note-${n._id}`}
                      sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                    >
                      <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Note
                        </Typography>
                      </TableCell>
                      {renderRadioCell(
                        !include,
                        false,
                        () => onToggleNote(n._id),
                        emDash,
                      )}
                      {renderRadioCell(
                        include,
                        false,
                        () => onToggleNote(n._id),
                        content,
                      )}
                    </TableRow>
                  );
                })}
              </Fragment>
            )}

            {/* Cautions - same shape as notes */}
            {(currentCautions.length > 0 || sourceCautions.length > 0) && (
              <Fragment key="__cautions">
                <TableRow
                  sx={{ backgroundColor: theme.palette.action.selected }}
                >
                  <TableCell colSpan={3}>
                    <Typography variant="body2" fontWeight={600}>
                      Cautions
                    </Typography>
                  </TableCell>
                </TableRow>

                {currentCautions.map((c: any) => {
                  const label = renderCautionLabel(c);
                  const keep = cautionChecks[String(c._id)] !== false;
                  const content = (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {label || '(empty)'}
                    </Typography>
                  );
                  return (
                    <TableRow
                      key={`current-caution-${c._id}`}
                      sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                    >
                      <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Caution
                        </Typography>
                      </TableCell>
                      {renderRadioCell(
                        keep,
                        false,
                        () => onToggleCaution(String(c._id)),
                        content,
                      )}
                      {renderRadioCell(
                        !keep,
                        false,
                        () => onToggleCaution(String(c._id)),
                        emDash,
                      )}
                    </TableRow>
                  );
                })}

                {sourceCautions.map((c: any) => {
                  const label = renderCautionLabel(c);
                  const include = !!cautionChecks[c._id];
                  const content = (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {label || '(empty)'}
                    </Typography>
                  );
                  return (
                    <TableRow
                      key={`source-caution-${c._id}`}
                      sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                    >
                      <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Caution
                        </Typography>
                      </TableCell>
                      {renderRadioCell(
                        !include,
                        false,
                        () => onToggleCaution(c._id),
                        emDash,
                      )}
                      {renderRadioCell(
                        include,
                        false,
                        () => onToggleCaution(c._id),
                        content,
                      )}
                    </TableRow>
                  );
                })}
              </Fragment>
            )}

            {/* Flags - one row per unique flag across both records, excluding
                overlap. Overlap (same flag on both) is omitted: the merged
                record will keep that flag automatically (target's copy stays;
                source's is soft-deleted server-side), so there's no diff to
                surface.
                Current-only: current shows the flag (disabled, checked),
                  matched shows a dash.
                Source-only: current shows a dash, matched shows the flag
                  with a toggleable radio (default off — user opts in). */}
            {(() => {
              const flagRef = (f: any): string =>
                String(f?.flag?._id ?? f?.flag ?? '');
              const sourceRefs = new Set(
                sourceFlags.map((f: any) => flagRef(f)),
              );
              const currentRefs = new Set(
                currentFlags.map((f: any) => flagRef(f)),
              );
              const currentOnly = currentFlags.filter(
                (f: any) => !sourceRefs.has(flagRef(f)),
              );
              const sourceOnly = sourceFlags.filter(
                (f: any) => !currentRefs.has(flagRef(f)),
              );

              if (currentOnly.length === 0 && sourceOnly.length === 0) {
                return null;
              }

              const labelOf = (f: any) => f?.flag?.name ?? f?.flag?.code ?? '';
              const renderLabel = (text: string) => (
                <Typography variant="body2">{text || '(empty)'}</Typography>
              );

              return (
                <Fragment key="__flags">
                  <TableRow
                    sx={{ backgroundColor: theme.palette.action.selected }}
                  >
                    <TableCell colSpan={3}>
                      <Typography variant="body2" fontWeight={600}>
                        Flags
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {currentOnly.map((f: any) => {
                    const keep = flagChecks[String(f._id)] !== false;
                    return (
                      <TableRow
                        key={`current-flag-${f._id}`}
                        sx={{
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Flag
                          </Typography>
                        </TableCell>
                        {renderRadioCell(
                          keep,
                          false,
                          () => onToggleFlag(String(f._id)),
                          renderLabel(labelOf(f)),
                        )}
                        {renderRadioCell(
                          !keep,
                          false,
                          () => onToggleFlag(String(f._id)),
                          emDash,
                        )}
                      </TableRow>
                    );
                  })}

                  {sourceOnly.map((f: any) => {
                    const include = !!flagChecks[f._id];
                    return (
                      <TableRow
                        key={`source-flag-${f._id}`}
                        sx={{
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Flag
                          </Typography>
                        </TableCell>
                        {renderRadioCell(
                          !include,
                          false,
                          () => onToggleFlag(f._id),
                          emDash,
                        )}
                        {renderRadioCell(
                          include,
                          false,
                          () => onToggleFlag(f._id),
                          renderLabel(labelOf(f)),
                        )}
                      </TableRow>
                    );
                  })}
                </Fragment>
              );
            })()}

            {/* System flags (WP, etc.) — informational only. The BE registry
                rewriter reassigns these automatically on merge, so neither
                side participates in opt-in. Render with disabled radios in
                the same pattern as related records so the user can see what
                will transfer. */}
            {(currentSystemFlags.length > 0 ||
              sourceSystemFlags.length > 0) && (
              <Fragment key="__system_flags">
                <TableRow
                  sx={{ backgroundColor: theme.palette.action.selected }}
                >
                  <TableCell colSpan={3}>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        System flags
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`System flags transfer automatically and can't be edited here.`}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>

                {currentSystemFlags.map((f: any) => (
                  <TableRow
                    key={`current-system-flag-${f._id}`}
                    sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                  >
                    <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Flag
                      </Typography>
                    </TableCell>
                    {renderRadioCell(
                      true,
                      true,
                      () => {},
                      <Typography variant="body2">
                        {f?.flag?.name ?? f?.flag?.code ?? '(empty)'}
                      </Typography>,
                    )}
                    {renderRadioCell(false, true, () => {}, emDash)}
                  </TableRow>
                ))}

                {sourceSystemFlags.map((f: any) => (
                  <TableRow
                    key={`source-system-flag-${f._id}`}
                    sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                  >
                    <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Flag
                      </Typography>
                    </TableCell>
                    {renderRadioCell(false, true, () => {}, emDash)}
                    {renderRadioCell(
                      true,
                      true,
                      () => {},
                      <Typography variant="body2">
                        {f?.flag?.name ?? f?.flag?.code ?? '(empty)'}
                      </Typography>,
                    )}
                  </TableRow>
                ))}
              </Fragment>
            )}

            {/* Related records — informational only on both sides. All related
                records are preserved on the target after merge; the user can
                no longer opt out per-record. */}
            {(currentRelatedRecords.length > 0 ||
              relatedRecords.length > 0) && (
              <Fragment key="__related">
                <TableRow
                  sx={{ backgroundColor: theme.palette.action.selected }}
                >
                  <TableCell colSpan={3}>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={600}>
                        Related records
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        All related records will be preserved on the merged
                        record.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>

                {currentRelatedRecords.map((r: any) => (
                  <TableRow
                    key={`current-related-${r._id}`}
                    sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                  >
                    <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Related record
                      </Typography>
                    </TableCell>
                    {renderRadioCell(
                      true,
                      true,
                      () => {},
                      renderRelatedContent(r),
                    )}
                    {renderRadioCell(false, true, () => {}, emDash)}
                  </TableRow>
                ))}

                {relatedRecords.map((r: any) => (
                  <TableRow
                    key={`source-related-${r._id}`}
                    sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                  >
                    <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Related record
                      </Typography>
                    </TableCell>
                    {renderRadioCell(false, true, () => {}, emDash)}
                    {renderRadioCell(
                      true,
                      true,
                      () => {},
                      renderRelatedContent(r),
                    )}
                  </TableRow>
                ))}
              </Fragment>
            )}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};
